import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";

const CardioSelectDialog = ({ cardioGoal, open, isEdit, onDialogClosed }) => {
  const [goal, setGoal] = React.useState(cardioGoal || {});

  React.useEffect(() => {
    if (cardioGoal) {
      // When initializing, make sure we migrate any 'session' value to 'sessions' and 'hr' to 'targetHR'
      const updatedGoal = { ...cardioGoal };
      if (updatedGoal.session && !updatedGoal.sessions) {
        updatedGoal.sessions = updatedGoal.session;
      }
      if (updatedGoal.hr && !updatedGoal.targetHR) {
        updatedGoal.targetHR = updatedGoal.hr;
      }
      setGoal(updatedGoal);
    }
  }, [cardioGoal]);

  const intensityLevels = ["Low", "Medium", "High", "Very High"];

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? "Edit Cardio Goal" : "Add Cardio Goal"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Goal Name"
                variant="outlined"
                value={goal.name || ""}
                onChange={(e) => setGoal({ ...goal, name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sessions per Week"
                variant="outlined"
                type="number"
                value={goal.sessions || ""}
                onChange={(e) => {
                  const value =
                    e.target.value === "" ? "" : Number(e.target.value);
                  setGoal({ ...goal, sessions: value });
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Duration (e.g. '30 minutes')"
                variant="outlined"
                value={goal.duration || ""}
                onChange={(e) => setGoal({ ...goal, duration: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Intensity</InputLabel>
                <Select
                  value={goal.intensity || "Medium"}
                  onChange={(e) =>
                    setGoal({ ...goal, intensity: e.target.value })
                  }
                  label="Intensity"
                >
                  {intensityLevels.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Target Heart Rate (e.g. '120-140')"
                variant="outlined"
                value={goal.targetHR || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setGoal({ ...goal, targetHR: value });
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes (optional)"
                variant="outlined"
                multiline
                rows={3}
                value={goal.notes || ""}
                onChange={(e) => setGoal({ ...goal, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={() => onDialogClosed(null)}
          variant="outlined"
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            // Before saving, ensure we're only using standardized field names
            const cleanedGoal = { ...goal };
            // Migrate session to sessions
            if (cleanedGoal.session) {
              // If no sessions value exists, copy from session
              if (!cleanedGoal.sessions) {
                cleanedGoal.sessions = cleanedGoal.session;
              }
              // Remove the old session property
              delete cleanedGoal.session;
            }

            // Migrate hr to targetHR
            if (cleanedGoal.hr) {
              // If no targetHR value exists, copy from hr
              if (!cleanedGoal.targetHR) {
                cleanedGoal.targetHR = cleanedGoal.hr;
              }
              // Remove the old hr property
              delete cleanedGoal.hr;
            }

            onDialogClosed(cleanedGoal);
          }}
          variant="contained"
          color="primary"
          disabled={!goal.name}
        >
          {isEdit ? "Save Changes" : "Add Goal"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CardioSelectDialog;
